import { RecentlyViewed } from '@theme/recently-viewed-products';
import { sectionRenderer } from '@theme/section-renderer';

/**
 * Custom element for displaying recently viewed products in a slider
 * Uses the theme's built-in predictive-search section to fetch products
 */
class RecentlyViewedSlider extends HTMLElement {
  /**
   * The observer for the recently viewed products
   * @type {IntersectionObserver}
   */
  #intersectionObserver = new IntersectionObserver(
    (entries, observer) => {
      if (!entries[0]?.isIntersecting) return;

      observer.disconnect();
      this.#loadRecentlyViewed();
    },
    { rootMargin: '0px 0px 400px 0px' }
  );

  /**
   * An abort controller for the active fetch (if there is one)
   * @type {AbortController | null}
   */
  #activeFetch = null;

  connectedCallback() {
    this.#intersectionObserver.observe(this);
  }

  /**
   * Load the recently viewed products
   */
  async #loadRecentlyViewed() {
    const viewedProducts = RecentlyViewed.getProducts();
    
    if (viewedProducts.length === 0) {
      this.classList.add('hidden');
      return;
    }

    const { sectionId, maxProducts } = this.dataset;
    const maxProductsNum = parseInt(maxProducts || '10', 10);
    const limitedProducts = viewedProducts.slice(0, maxProductsNum);
    
    // Use section-recently-viewed-products section (custom section that uses content_for for product cards)
    const targetSectionId = sectionId || 'section-recently-viewed-products';

    try {
      // Use search_url to trigger Shopify's search API (same as predictive-search.js)
      // This ensures search.results is populated with the products
      const url = new URL(Theme.routes.search_url, location.origin);
      url.searchParams.set('q', limitedProducts.map((id) => `id:${id}`).join(' OR '));
      url.searchParams.set('resources[type]', 'product');
      // Also pass product_ids as a parameter for parsing in Liquid (backup method)
      url.searchParams.set('product_ids', limitedProducts.join(','));

      this.#activeFetch?.abort();
      this.#activeFetch = new AbortController();

      // Get section HTML using section renderer
      const sectionHTML = await sectionRenderer.getSectionHTML(targetSectionId, false, url);
      
      if (this.#activeFetch.signal.aborted) return;

      // Parse the HTML to extract the slideshow content
      const parser = new DOMParser();
      const doc = parser.parseFromString(sectionHTML, 'text/html');
      
      // First, try to find the recently-viewed-slider element in the parsed HTML
      let recentlyViewedSlider = doc.querySelector('recently-viewed-slider');
      
      // If found, extract the slideshow-component from inside it
      if (recentlyViewedSlider) {
        const slideshow = recentlyViewedSlider.querySelector('slideshow-component');
        if (slideshow) {
          // Use outerHTML to include the slideshow-component tag itself
          this.innerHTML = slideshow.outerHTML;
          this.#notifyContentReady();
          return;
        }
        // If no slideshow found inside, use the content of recently-viewed-slider
        if (recentlyViewedSlider.innerHTML.trim().length > 0) {
          this.innerHTML = recentlyViewedSlider.innerHTML;
          this.#notifyContentReady();
          return;
        }
      }
      
      // Fallback: try to find slideshow-component directly in the document
      const slideshow = doc.querySelector('slideshow-component');
      if (slideshow) {
        this.innerHTML = slideshow.outerHTML;
        this.#notifyContentReady();
        return;
      }
      
      // Fallback: try to find div with class containing "recently-viewed" or "tabs-products-sliders"
      let recentlyViewedDiv = doc.querySelector('[class*="recently-viewed"]');
      
      if (!recentlyViewedDiv) {
        // Try finding by section ID pattern
        recentlyViewedDiv = doc.querySelector(`[class*="${targetSectionId}"]`);
      }
      
      if (!recentlyViewedDiv) {
        // Try finding the section element
        recentlyViewedDiv = doc.querySelector(`.shopify-section-${targetSectionId.replace('section-', '')}`);
      }
      
      if (!recentlyViewedDiv) {
        // Last resort: use body
        recentlyViewedDiv = doc.body;
      }
      
      
      if (!recentlyViewedDiv || recentlyViewedDiv === doc.body) {
        // If we can't find the div, try to use the body content directly
        if (doc.body.innerHTML.trim().length > 0) {
          this.innerHTML = doc.body.innerHTML;
          this.#notifyContentReady();
          return;
        }
        this.classList.add('hidden');
        return;
      }

      // Extract the slideshow component or its content
      const slideshowInDiv = recentlyViewedDiv.querySelector('slideshow-component');
      
      if (slideshowInDiv) {
        // Use outerHTML to include the slideshow-component tag itself
        this.innerHTML = slideshowInDiv.outerHTML;
        this.#notifyContentReady();
      } else if (recentlyViewedDiv.innerHTML.trim().length > 0) {
        // If no slideshow found, try to extract the entire div content
        this.innerHTML = recentlyViewedDiv.innerHTML;
        this.#notifyContentReady();
      } else {
        this.classList.add('hidden');
      }
      
    } catch (error) {
      if (error.name !== 'AbortError') {
        this.classList.add('hidden');
      }
    } finally {
      this.#activeFetch = null;
    }
  }

  /**
   * Fires after innerHTML is set. Double rAF waits for custom elements to upgrade and layout
   * before listeners init third-party widgets (e.g. Swym).
   */
  #notifyContentReady() {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.dispatchEvent(
          new CustomEvent('recently-viewed-slider:loaded', {
            bubbles: true,
            detail: { sectionId: this.dataset.sectionId || '' },
          })
        );
      });
    });
  }
}

if (!customElements.get('recently-viewed-slider')) {
  customElements.define('recently-viewed-slider', RecentlyViewedSlider);
}

